import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Button from '../../components/ui/Button';
import VideoTable from './components/VideoTable';
import VideoFilters from './components/VideoFilters';
import VideoStats from './components/VideoStats';
import { getAllVideosWithAnalytics } from '../../services/videoService';
import Icon from '../../components/AppIcon';

const VideoManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, isSuperUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    song: '',
    section: '',
    talent: ''
  });

  // Redirect if not superuser
  useEffect(() => {
    if (user && !isSuperUser()) {
      navigate('/song-database-list');
    }
  }, [user, isSuperUser, navigate]);

  // Load videos
  const loadVideos = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: serviceError } = await getAllVideosWithAnalytics(sortBy, sortOrder);
      
      if (serviceError) {
        setError(serviceError);
        setVideos([]);
      } else {
        setVideos(data || []);
      }
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos. Please try again.');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user && isSuperUser()) {
      loadVideos();
    }
  }, [user, isSuperUser, sortBy, sortOrder]);

  // Apply filters
  useEffect(() => {
    let filtered = [...videos];

    // Filter by song
    if (filters?.song?.trim()) {
      filtered = filtered?.filter(video => {
        const songTitle = video?.song_analyses?.overview?.songTitle?.toLowerCase() || '';
        const musical = video?.song_analyses?.overview?.musical?.toLowerCase() || '';
        const searchTerm = filters?.song?.toLowerCase();
        return songTitle?.includes(searchTerm) || musical?.includes(searchTerm);
      });
    }

    // Filter by section
    if (filters?.section?.trim()) {
      filtered = filtered?.filter(video => 
        video?.level?.toLowerCase()?.includes(filters?.section?.toLowerCase())
      );
    }

    // Filter by talent
    if (filters?.talent?.trim()) {
      filtered = filtered?.filter(video => {
        const talentName = `${video?.talent_profiles?.first_name || ''} ${video?.talent_profiles?.last_name || ''}`?.toLowerCase();
        return talentName?.includes(filters?.talent?.toLowerCase());
      });
    }

    setFilteredVideos(filtered);
  }, [videos, filters]);

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = () => {
    loadVideos();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const formatWatchTime = (totalSeconds) => {
    if (!totalSeconds) return '0m 0s';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  // Calculate overall stats
  const stats = {
    totalVideos: videos?.length || 0,
    totalPlays: videos?.reduce((sum, video) => sum + (video?.analytics?.totalPlays || 0), 0),
    totalWatchTime: videos?.reduce((sum, video) => sum + (video?.analytics?.totalWatchTime || 0), 0),
    avgEngagement: videos?.length > 0 
      ? Math.round((videos?.reduce((sum, video) => sum + (video?.analytics?.firstFiveSecondsPlayed || 0), 0) / videos?.length) * 100) 
      : 0
  };

  if (!user || !isSuperUser()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
            <p className="text-blue-100">You need SuperUser privileges to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Video Management</h1>
            <p className="text-blue-100 mt-2">
              Monitor video uploads, analytics, and engagement across all songs
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center space-x-2 border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
          >
            <Icon name="RefreshCw" size={18} />
            <span>Refresh</span>
          </Button>
        </div>

        {/* Statistics */}
        <VideoStats 
          stats={stats}
          formatWatchTime={formatWatchTime}
        />

        {/* Filters */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-8">
          <VideoFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <Icon name="AlertCircle" className="text-red-800" size={20} />
              <div>
                <h3 className="font-medium text-red-800">Error</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="large" />
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {filteredVideos?.length > 0 ? (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden">
                <VideoTable
                  videos={filteredVideos}
                  formatDuration={formatDuration}
                  formatWatchTime={formatWatchTime}
                  onRefresh={handleRefresh}
                />
              </div>
            ) : (
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
                <div className="flex justify-center mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-xl">
                    <Icon name="Video" className="text-blue-100" size={32} />
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">
                  {videos?.length > 0 ? 'No Videos Match Your Filters' : 'No Videos Found'}
                </h3>
                
                <p className="text-blue-100 mb-6">
                  {videos?.length > 0 
                    ? 'Try adjusting your search filters to find more videos.' :'Videos will appear here once they are uploaded to songs. Go to the song editor to add videos.'
                  }
                </p>

                {videos?.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={() => setFilters({ song: '', section: '', talent: '' })}
                    className="flex items-center space-x-2 border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                  >
                    <Icon name="X" size={18} />
                    <span>Clear Filters</span>
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default VideoManagementDashboard;